import type { CSSProperties, ReactNode } from "react";

type SkeletonProps = {
  className?: string;
  style?: CSSProperties;
};

/**
 * Skeleton — pulsing placeholder block shared by every route's loading.tsx
 * (W6). `animate-pulse` is Tailwind's default keyframe; `motion-reduce:
 * animate-none` opts the block out under prefers-reduced-motion (belt and
 * braces — the global reduced-motion block in globals.css already collapses
 * animation/transition durations app-wide, this makes the intent explicit
 * on the primitive itself). Background defaults to the ARENA GRIT
 * surface-3 token; pass `style.background` to override (e.g. the portal's
 * white-overlay tone via `rgba(255,255,255,0.06)`).
 */
export function Skeleton({ className = "", style }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={`animate-pulse motion-reduce:animate-none rounded-md ${className}`}
      style={{ background: "var(--color-surface-3)", ...style }}
    />
  );
}

/**
 * SkeletonCard — a DesignCard-shaped pulsing container (ARENA GRIT
 * surface-1 + border, matching components/design/Card.tsx's undecorated
 * look) for card-grid loading states. Compose a route's own row of
 * `Skeleton` blocks as children to approximate that page's card shape.
 */
export function SkeletonCard({
  className = "",
  style,
  children,
}: SkeletonProps & { children?: ReactNode }) {
  return (
    <div
      className={className}
      style={{
        background: "var(--color-surface-1)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-lg)",
        padding: 16,
        ...style,
      }}
    >
      {children}
    </div>
  );
}
